import { NavLink, Outlet, useNavigate } from "react-router-dom";
import { LayoutDashboard, Users, ArrowLeft, LogOut, ShieldCheck, TrendingUp, Shield, UserCog, BrainCircuit, HeartPulse, Swords, Activity } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { LOGO_URL } from "@/lib/nav";
import { hasCap, ROLE_LABEL } from "@/lib/roles";
import { cn } from "@/lib/utils";

const NAV = [
    { to: "/admin", label: "Overview", icon: LayoutDashboard, end: true, cap: "overview", testid: "admin-nav-overview" },
    { to: "/admin/business", label: "Business", icon: TrendingUp, cap: "business", testid: "admin-nav-business" },
    { to: "/admin/staff", label: "Staff", icon: UserCog, cap: "staff", testid: "admin-nav-staff" },
    { to: "/admin/teams", label: "Teams", icon: Shield, cap: "teams", testid: "admin-nav-teams" },
    { to: "/admin/users", label: "Users", icon: Users, cap: "users", testid: "admin-nav-users" },
    { to: "/admin/tournaments", label: "Tournaments", icon: Swords, cap: "tournaments", testid: "admin-nav-tournaments" },
    { to: "/admin/ai-learning", label: "AI Learning", icon: BrainCircuit, cap: "ai_learning", testid: "admin-nav-ai-learning" },
    { to: "/admin/capture-health", label: "Capture Health", icon: HeartPulse, cap: "capture_health", testid: "admin-nav-capture-health" },
    { to: "/admin/developer-monitor", label: "Developer Monitor", icon: Activity, cap: "config", testid: "admin-nav-developer-monitor" },
];

export function AdminLayout() {
    const { user, logout } = useAuth();
    const navigate = useNavigate();
    const nav = NAV.filter((n) => hasCap(user, n.cap));

    return (
        <div className="flex min-h-screen bg-obsidian">
            <aside className="hidden lg:flex flex-col w-60 shrink-0 border-r border-border bg-[#0c0c0c] sticky top-0 h-screen">
                <div className="flex items-center gap-2.5 px-5 h-16 border-b border-border">
                    <div className="w-8 h-8 rounded-sm bg-black border border-white/10"
                        style={{ backgroundImage: `url(${LOGO_URL})`, backgroundSize: "175%", backgroundPosition: "center 32%" }} />
                    <div className="leading-none">
                        <div className="font-heading font-extrabold text-[14px] text-white">Owner<span className="text-success"> Portal</span></div>
                        <div className="text-[9px] uppercase tracking-[0.2em] text-muted-foreground mt-0.5">WhiteWater AI</div>
                    </div>
                </div>
                <nav className="flex-1 px-3 py-5 space-y-1">
                    {nav.map((n) => (
                        <NavLink key={n.to} to={n.to} end={n.end} data-testid={n.testid}
                            className={({ isActive }) => cn("flex items-center gap-3 px-3 py-2 rounded-sm text-sm transition-colors",
                                isActive ? "bg-success/10 text-white" : "text-muted-foreground hover:text-white hover:bg-white/5")}>
                            {({ isActive }) => (<><n.icon className={cn("w-[18px] h-[18px]", isActive && "text-success")} /> {n.label}</>)}
                        </NavLink>
                    ))}
                </nav>
                <div className="px-3 py-4 border-t border-border space-y-1">
                    <button onClick={() => navigate("/dashboard")} data-testid="admin-back-to-app"
                        className="w-full flex items-center gap-3 px-3 py-2 rounded-sm text-sm text-muted-foreground hover:text-white hover:bg-white/5">
                        <ArrowLeft className="w-[18px] h-[18px]" /> Back to App
                    </button>
                    <button onClick={logout} data-testid="admin-logout"
                        className="w-full flex items-center gap-3 px-3 py-2 rounded-sm text-sm text-muted-foreground hover:text-alert hover:bg-alert/10">
                        <LogOut className="w-[18px] h-[18px]" /> Sign out
                    </button>
                </div>
            </aside>

            <div className="flex-1 flex flex-col min-w-0">
                <header className="sticky top-0 z-30 h-16 border-b border-border bg-[#0a0a0a]/90 backdrop-blur-md flex items-center justify-between px-4 lg:px-7">
                    <div className="flex items-center gap-2 text-sm">
                        <ShieldCheck className="w-4 h-4 text-success" />
                        <span className="font-mono uppercase tracking-wider text-xs text-muted-foreground">Owner Portal</span>
                    </div>
                    <div className="flex items-center gap-3">
                        <button onClick={() => navigate("/dashboard")} className="lg:hidden text-xs text-volt" data-testid="admin-back-mobile">App</button>
                        <span className="text-sm text-white">{user?.name}</span>
                        <span className="text-[10px] font-mono uppercase px-2 py-0.5 rounded-sm border border-success/40 text-success" data-testid="admin-role-badge">{ROLE_LABEL[user?.role] || "Staff"}</span>
                    </div>
                </header>
                <main className="flex-1 px-4 lg:px-7 py-6 lg:py-8 max-w-[1400px] w-full mx-auto">
                    <Outlet />
                </main>
            </div>
        </div>
    );
}
